/**
 * Dichotomie — logique pure (Boîte à outils, 2nde → prépa).
 *
 * f continue et strictement monotone sur [a ; b], f(a) et f(b) de signes
 * contraires : f(x) = 0 a une unique solution α dans ]a ; b[ (corollaire du
 * théorème des valeurs intermédiaires). On coupe en deux au milieu m : si
 * f(a) × f(m) ≤ 0, α est dans [a ; m], sinon dans [m ; b]. Chaque étape
 * divise l'amplitude par 2 : il faut n étapes avec (b − a)/2ⁿ ≤ p, soit
 * 2ⁿ ≥ (b − a)/p (le programme s'arrête dès que b − a ≤ p).
 */

export interface Problem {
  id: string;
  /** écriture de f(x) sur la pastille */
  label: string;
  /** f(x) lue par le lecteur d'écran */
  spoken: string;
  /** « x² − 2 » */
  text: string;
  f: (x: number) => number;
  a: number;
  b: number;
  /** la solution, écrite exactement quand on la connaît (« √2 », « ln 3 ») */
  rootName: string;
  /** l'équation qu'elle résout (« x² = 2 ») */
  equation: string;
  root: number;
  /** pourquoi f est strictement monotone sur [a ; b] */
  mono: string;
  /** f en Python, pour le programme affiché */
  py: string;
  /** fonctions de math à importer en Python */
  imports: string[];
  /** rang minimal (2nde = 5, 1re = 6, Tle = 7) */
  minRank: number;
}

export const PROBLEMS: readonly Problem[] = [
  {
    id: 'sqrt2',
    label: 'x² − 2',
    spoken: 'x au carré moins 2',
    text: 'x² − 2',
    f: (x) => x * x - 2,
    a: 1,
    b: 2,
    rootName: '√2',
    equation: 'x² = 2',
    root: Math.SQRT2,
    mono: 'x ↦ x² est strictement croissante sur [1 ; 2]',
    py: 'x**2 - 2',
    imports: [],
    minRank: 5,
  },
  {
    id: 'cubic',
    label: 'x³ + x − 1',
    spoken: 'x au cube plus x moins 1',
    text: 'x³ + x − 1',
    f: (x) => x * x * x + x - 1,
    a: 0,
    b: 1,
    rootName: 'α',
    equation: 'x³ + x = 1',
    root: 0.6823278038280193,
    mono: 'x³ et x sont strictement croissantes, leur somme aussi',
    py: 'x**3 + x - 1',
    imports: [],
    minRank: 5,
  },
  {
    id: 'ln3',
    label: 'eˣ − 3',
    spoken: 'exponentielle de x moins 3',
    text: 'eˣ − 3',
    f: (x) => Math.exp(x) - 3,
    a: 1,
    b: 2,
    rootName: 'ln 3',
    equation: 'eˣ = 3',
    root: Math.log(3),
    mono: 'la fonction exponentielle est strictement croissante',
    py: 'exp(x) - 3',
    imports: ['exp'],
    minRank: 7,
  },
  {
    id: 'cos',
    label: 'cos x − x',
    spoken: 'cosinus de x moins x',
    text: 'cos x − x',
    f: (x) => Math.cos(x) - x,
    a: 0,
    b: 1,
    rootName: 'α',
    equation: 'cos x = x',
    root: 0.7390851332151607,
    mono: 'f′(x) = −sin x − 1 < 0 sur [0 ; 1]',
    py: 'cos(x) - x',
    imports: ['cos'],
    minRank: 7,
  },
  {
    // f(m) = 0 à la deuxième étape : m est la solution exacte
    id: 'exact',
    label: 'x² − 1,5625',
    spoken: 'x au carré moins 1,5625',
    text: 'x² − 1,5625',
    f: (x) => x * x - 1.5625,
    a: 1,
    b: 2,
    rootName: '1,25',
    equation: 'x² = 1,5625',
    root: 1.25,
    mono: 'x ↦ x² est strictement croissante sur [1 ; 2]',
    py: 'x**2 - 1.5625',
    imports: [],
    minRank: 7,
  },
];

export const problemById = (id: string) => PROBLEMS.find((p) => p.id === id) ?? PROBLEMS[0]!;
export const problemsFor = (rank: number) => PROBLEMS.filter((p) => p.minRank <= rank);

/** signe : −1, 0 ou 1 (0 seulement pour une valeur nulle à la précision machine) */
export const sgn = (v: number) => (Math.abs(v) < 1e-12 ? 0 : v > 0 ? 1 : -1);

/** signe écrit : « + », « − », « 0 » */
export const sign = (v: number) => (sgn(v) === 0 ? '0' : sgn(v) > 0 ? '+' : '−');

/** comparaison à 0 : « < 0 », « > 0 », « = 0 » */
export const cmp0 = (v: number) => (sgn(v) === 0 ? '= 0' : sgn(v) > 0 ? '> 0' : '< 0');

export interface Step {
  a: number;
  b: number;
  m: number;
  fa: number;
  fm: number;
  /** moitié gardée par le programme (test f(a) × f(m) ≤ 0) */
  keep: 'left' | 'right';
  /** f(m) = 0 : m est une solution exacte (les deux moitiés la contiennent) */
  exact: boolean;
}

/** une étape depuis [a ; b] : le milieu, le signe, la moitié gardée */
export function step(p: Problem, a: number, b: number): Step {
  const m = (a + b) / 2;
  const fa = p.f(a);
  const fm = p.f(m);
  return {
    a,
    b,
    m,
    fa,
    fm,
    keep: sgn(fa) * sgn(fm) <= 0 ? 'left' : 'right',
    exact: sgn(fm) === 0,
  };
}

/** l'intervalle après l'étape */
export const after = (s: Step): [number, number] => (s.keep === 'left' ? [s.a, s.m] : [s.m, s.b]);

/** condition d'arrêt du programme : la boucle tourne tant que b − a > p */
export const isDone = (a: number, b: number, prec: number) => b - a <= prec + 1e-15;

/** toutes les étapes jusqu'à b − a ≤ p (comme le programme : while b − a > p) */
export function run(p: Problem, prec: number, max = 40): Step[] {
  const out: Step[] = [];
  let a = p.a;
  let b = p.b;
  while (!isDone(a, b, prec) && out.length < max) {
    const s = step(p, a, b);
    out.push(s);
    [a, b] = after(s);
  }
  return out;
}

/** nombre d'étapes prévu : le plus petit n tel que (b − a)/2ⁿ ≤ p, soit 2ⁿ ≥ (b − a)/p */
export function stepsNeeded(p: Problem, prec: number): number {
  const ratio = (p.b - p.a) / prec;
  let n = 0;
  while (2 ** n < ratio - 1e-9) n++;
  return n;
}

/** « 1,4375 » : écriture décimale exacte des bornes (ce sont des décimaux dyadiques) */
export function dec(v: number, max = 10): string {
  let s = v.toFixed(max);
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s.replace('.', ',').replace('-', '−');
}

/** borne arrondie vers l'extérieur à `d` décimales (encadrement « de copie ») */
export function outward(v: number, d: number, dir: 'down' | 'up'): string {
  const k = 10 ** d;
  const r = (dir === 'down' ? Math.floor(v * k + 1e-9) : Math.ceil(v * k - 1e-9)) / k;
  return dec(r, d);
}

/** nombre de décimales d'une précision 10⁻ᵈ */
export const decimalsOf = (prec: number) => Math.max(0, Math.round(-Math.log10(prec)));

/** le programme Python affiché (lignes numérotées dans l'outil) */
export function PROGRAM(p: Problem, prec: string): string[] {
  return [
    ...(p.imports.length ? [`from math import ${p.imports.join(', ')}`, ''] : []),
    'def f(x):',
    `    return ${p.py}`,
    '',
    'def dichotomie(a, b, p):',
    '    while b - a > p:',
    '        m = (a + b) / 2',
    '        if f(a) * f(m) <= 0:',
    '            b = m',
    '        else:',
    '            a = m',
    '    return a, b',
    '',
    `print(dichotomie(${String(p.a)}, ${String(p.b)}, ${prec}))`,
  ];
}

/** numéro (à partir de 1) de la ligne « while » dans le programme de p */
const whileLine = (p: Problem) => (p.imports.length ? 7 : 5);

export type Phase = 'start' | 'mid' | 'tested' | 'done';

/**
 * Lignes exécutées, selon le moment : milieu calculé (while, m = …), test
 * fait (if, puis b = m ou else / a = m), fin (while faux, return).
 */
export function linesFor(p: Problem, phase: Phase, s: Step | null): number[] {
  const w = whileLine(p);
  if (phase === 'done') return [w, w + 6];
  if (phase === 'mid') return [w, w + 1];
  if (phase === 'tested' && s) return s.keep === 'left' ? [w + 2, w + 3] : [w + 2, w + 4, w + 5];
  return [];
}
